import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql, Link } from 'gatsby';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';
import GameEmbed from '@components/game-embed';
import Modal from '@components/modal';
import { sortEdgesByOrder } from '@utils/sort';

const StyledGamesSection = styled.section`
  max-width: 1100px;

  .list {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    margin-top: 40px;

    @media (max-width: 1024px) {
      grid-template-columns: 1fr;
    }
  }
`;

const StyledViewAll = styled.div`
  margin-top: 28px;
  display: flex;
  justify-content: center;

  a {
    ${({ theme }) => theme.mixins.bigButton};
  }
`;

const Games = ({
  limit = 4,
  showViewAllLink = true,
  showHeading = true,
  sectionId = 'games',
  viewAllHref = '/games',
  viewAllText = 'View all games',
}) => {
  const data = useStaticQuery(graphql`
    query {
      games: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/user/games/" } }
        sort: { fields: [frontmatter___order, frontmatter___title], order: ASC }
      ) {
        edges {
          node {
            frontmatter {
              title
              order
              tech
              external
              embedUrl
              cover {
                childImageSharp {
                  gatsbyImageData(width: 900, placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                }
              }
            }
            html
          }
        }
      }
    }
  `);

  const games = sortEdgesByOrder(
    data.games.edges.filter(({ node }) => node),
    (a, b) =>
      String(a?.node?.frontmatter?.title || '').localeCompare(
        String(b?.node?.frontmatter?.title || ''),
      ),
  );
  if (!games || games.length === 0) {
    return null;
  }

  const [modal, setModal] = useState({ isOpen: false, title: '', html: '' });
  const openModal = ({ title, html }) => setModal({ isOpen: true, title, html });
  const closeModal = () => setModal(m => ({ ...m, isOpen: false }));

  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const gamesToShow = typeof limit === 'number' ? games.slice(0, limit) : games;

  return (
    <StyledGamesSection id={sectionId} ref={revealContainer}>
      {showHeading && <h2 className="numbered-heading">Games</h2>}

      <p>
        A few small experiments you can play right here in the browser. Each one started as a tiny
        idea, then grew into a mini-challenge with its own vibe.
      </p>

      <ul className="list">
        {gamesToShow.map(({ node }, i) => (
          <li key={i}>
            <GameEmbed
              title={node.frontmatter.title}
              storyHtml={node.html}
              embedUrl={node.frontmatter.embedUrl}
              externalUrl={node.frontmatter.external}
              tech={node.frontmatter.tech}
              cover={node.frontmatter.cover}
              onReadAbout={openModal}
            />
          </li>
        ))}
      </ul>

      {showViewAllLink && (
        <StyledViewAll>
          <Link to={viewAllHref}>{viewAllText}</Link>
        </StyledViewAll>
      )}

      <Modal isOpen={modal.isOpen} title={modal.title} onClose={closeModal}>
        <div dangerouslySetInnerHTML={{ __html: modal.html }} />
      </Modal>
    </StyledGamesSection>
  );
};

Games.propTypes = {
  limit: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  showViewAllLink: PropTypes.bool,
  showHeading: PropTypes.bool,
  sectionId: PropTypes.string,
  viewAllHref: PropTypes.string,
  viewAllText: PropTypes.string,
};

export default Games;
